import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Loader2, Image as ImageIcon, Sparkles, Link2, Shirt, FolderOpen,
} from "lucide-react";
import { Header } from "../components/Header";
import MediaPicker from "../components/MediaPicker";
import { api, formatApiErrorDetail, uploadImage, resolveAsset } from "../lib/api";

function isHomePath(to) {
  if (!to) return true;
  const trimmed = to.trim().toLowerCase();
  return trimmed === "/" || trimmed === "/home" || trimmed === "";
}

// Single source of truth for what appears in the home "Explore" rotation
// widget (see HomeRotatingTiles.jsx) — merges real nav-menu items and
// every page tile (across all page_keys) into one list so an admin never
// has to hunt across the separate Nav menu / Page tiles screens to find
// the checkbox or image for a given link.
export default function AdminExploreRotation() {
  const [navItems, setNavItems] = useState(null);
  const [tiles, setTiles] = useState(null);
  const [error, setError] = useState("");
  const [pickerFor, setPickerFor] = useState(null); // { kind, id } | null
  const [savingKey, setSavingKey] = useState("");
  const [uploadingKey, setUploadingKey] = useState("");

  const refresh = async () => {
    try {
      const [navRes, tileRes] = await Promise.all([
        api.get("/admin/nav-items"),
        api.get("/admin/page-tiles"),
      ]);
      setNavItems((navRes.data || []).filter((n) => n.kind === "nav" && !isHomePath(n.to)));
      setTiles(tileRes.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const rows = useMemo(() => {
    if (!navItems || !tiles) return null;
    const navRows = navItems.map((n) => ({
      kind: "nav",
      id: n.id,
      label: n.label,
      hint: n.to,
      image_url: n.image_url || "",
      explore_pickable: n.explore_pickable !== false,
    }));
    const tileRows = tiles.map((t) => ({
      kind: "tile",
      id: t.id,
      label: t.title || "Untitled tile",
      hint: `page_key=${t.page_key}`,
      image_url: t.image_url || "",
      explore_pickable: !!t.explore_pickable,
    }));
    return [...navRows, ...tileRows];
  }, [navItems, tiles]);

  const patch = async (kind, id, body) => {
    const path = kind === "nav" ? `/admin/nav-items/${id}` : `/admin/page-tiles/${id}`;
    await api.put(path, body);
  };

  const toggle = async (row) => {
    const key = `${row.kind}-${row.id}`;
    setSavingKey(key);
    // Optimistic flip.
    const setter = row.kind === "nav" ? setNavItems : setTiles;
    setter((prev) => prev.map((it) =>
      it.id === row.id ? { ...it, explore_pickable: !row.explore_pickable } : it
    ));
    try {
      await patch(row.kind, row.id, { explore_pickable: !row.explore_pickable });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setter((prev) => prev.map((it) =>
        it.id === row.id ? { ...it, explore_pickable: row.explore_pickable } : it
      ));
    } finally {
      setSavingKey("");
    }
  };

  const setImage = async (row, url) => {
    const key = `${row.kind}-${row.id}`;
    setSavingKey(key);
    const setter = row.kind === "nav" ? setNavItems : setTiles;
    setter((prev) => prev.map((it) => (it.id === row.id ? { ...it, image_url: url } : it)));
    try {
      await patch(row.kind, row.id, { image_url: url });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingKey("");
    }
  };

  const onUpload = async (row, file) => {
    if (!file) return;
    const key = `${row.kind}-${row.id}`;
    setUploadingKey(key);
    try {
      const res = await uploadImage(file);
      await setImage(row, res.url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploadingKey("");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]" data-testid="page-admin-explore-rotation">
      <Header />
      <main className="mx-auto max-w-4xl px-5 py-8 sm:px-8">
        <RouterLink to="/admin" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-body)] hover:text-[var(--brand-ink)]">
          <ArrowLeft size={14} /> Back to Admin
        </RouterLink>

        <div className="mt-3 flex items-center gap-2 text-brand-accent">
          <Sparkles size={14} strokeWidth={2.5} />
          <span className="label-kicker">Home Explore rotation</span>
        </div>
        <h1 className="mt-1 font-display text-3xl font-black tracking-tight sm:text-4xl">
          One place for what shows &amp; what it looks like.
        </h1>
        <p className="mt-2 text-sm text-[var(--brand-body)]">
          Every nav menu item and every page tile lives here. Tick "Include" to put it in
          the home Explore rotation, and set its image right here — no more hunting across
          the Nav menu / Page tiles screens.
        </p>

        {error ? (
          <p className="mt-4 rounded-md border border-[#E63946]/30 bg-[#E63946]/5 px-3 py-2 text-sm text-[#E63946]" data-testid="explore-rotation-error">
            {error}
          </p>
        ) : null}

        {!rows ? (
          <div className="mt-8 flex items-center gap-2 text-[var(--brand-body)]">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="mt-6 divide-y divide-black/5 rounded-md border border-black/10 bg-white">
            {rows.map((row) => {
              const key = `${row.kind}-${row.id}`;
              return (
                <div
                  key={key}
                  className="flex flex-wrap items-center gap-3 px-4 py-3"
                  data-testid={`explore-rotation-row-${key}`}
                >
                  <button
                    type="button"
                    onClick={() => setPickerFor({ kind: row.kind, id: row.id })}
                    className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-black/10 bg-[#F8FAFC]"
                    title="Pick from media library"
                    data-testid={`explore-rotation-pick-${key}`}
                  >
                    {row.image_url ? (
                      <img src={resolveAsset(row.image_url)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon size={16} className="text-[var(--brand-body)]" />
                    )}
                  </button>

                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setPickerFor({ kind: row.kind, id: row.id })}
                      className="inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1.5 text-[11px] font-semibold hover:border-[#0A0F17]"
                      data-testid={`explore-rotation-library-${key}`}
                    >
                      <FolderOpen size={12} /> Library
                    </button>
                    <label
                      className="inline-flex cursor-pointer items-center justify-center rounded-md border border-black/10 px-2 py-1.5 text-[11px] font-semibold hover:border-[#0A0F17]"
                      data-testid={`explore-rotation-upload-${key}`}
                    >
                      {uploadingKey === key ? <Loader2 size={12} className="animate-spin" /> : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onUpload(row, e.target.files?.[0])}
                      />
                    </label>
                  </div>

                  <div className="min-w-[160px] flex-1">
                    <p className="flex items-center gap-1.5 text-sm font-semibold">
                      {row.kind === "tile" ? <Shirt size={12} className="text-[var(--brand-body)]" /> : <Link2 size={12} className="text-[var(--brand-body)]" />}
                      {row.label}
                    </p>
                    <p className="label-kicker text-[var(--brand-body)]">
                      {row.kind === "tile" ? "Page tile" : "Nav item"} · {row.hint}
                    </p>
                  </div>

                  <label className="flex items-center gap-2 text-xs font-semibold">
                    <input
                      type="checkbox"
                      checked={row.explore_pickable}
                      onChange={() => toggle(row)}
                      data-testid={`explore-rotation-checkbox-${key}`}
                      className="h-4 w-4 accent-[#0A0F17]"
                    />
                    Include
                    {savingKey === key ? <Loader2 size={12} className="animate-spin text-[var(--brand-body)]" /> : null}
                  </label>
                </div>
              );
            })}
            {rows.length === 0 ? (
              <p className="px-4 py-6 text-sm text-[var(--brand-body)]">
                No nav items or page tiles yet.
              </p>
            ) : null}
          </div>
        )}
      </main>

      <MediaPicker
        open={!!pickerFor}
        onClose={() => setPickerFor(null)}
        onSelect={(url) => {
          const row = rows.find((r) => r.kind === pickerFor.kind && r.id === pickerFor.id);
          setPickerFor(null);
          if (row) setImage(row, url);
        }}
        accept="image"
      />
    </div>
  );
}
